import { Stack, Typography } from '@mui/material';
import { useIntl } from 'react-intl';

import {
  ApiWorkflowDialog,
  type ApiWorkflowDialogMessages,
  DialogMessageBox,
} from '@verantos-dev/shared-components';

import { useDeleteConceptGroupDialog } from '../hooks/useDeleteConceptGroupDialog';

interface DeleteConceptGroupDialogProps {
  projectId: string;
  conceptGroupId: string;
  name: string;
  onClose: () => void;
}

/**
 * Inline delete-confirmation dialog opened from a concept-group row on the
 * artifacts page. Lives here rather than reusing feature-concept-groups-list's
 * `DeleteConceptGroupModal` (no cycle blocks that import — cohorts already
 * import their own feature lib's modal on this same page): that modal is
 * built around the library page's `ConceptGroupsListDialogContext` (a
 * create/rename/delete dialog-set reducer) and re-looks-up the concept
 * group's name via a full org-wide `useConceptGroups()` scan — disproportionate
 * plumbing for a single Delete action when the row already carries the name.
 * Reuses the `conceptGroups.deleteConceptGroupModal.*` message ids (registered
 * app-wide via `conceptGroupsMessagesEn`) so copy stays in sync with the
 * standalone library modal without importing the feature lib.
 */
export const DeleteConceptGroupDialog = ({
  projectId,
  conceptGroupId,
  name,
  onClose,
}: DeleteConceptGroupDialogProps) => {
  const { formatMessage } = useIntl();
  const {
    onSubmit,
    closeDialog,
    isInUse,
    isSubmitting,
    isInitialLoading,
    submittingError,
    isValidForSubmit,
  } = useDeleteConceptGroupDialog({ projectId, conceptGroupId, onClose });

  const dialogMessages: ApiWorkflowDialogMessages = {
    loadingCancelButtonText: formatMessage({
      id: 'conceptGroups.deleteConceptGroupModal.cancelButton',
    }),
    loadingSaveButtonText: formatMessage({
      id: 'conceptGroups.deleteConceptGroupModal.deleteButton',
    }),
    entryCancelButtonText: formatMessage({
      id: 'conceptGroups.deleteConceptGroupModal.cancelButton',
    }),
    entrySaveButtonText: formatMessage({
      id: 'conceptGroups.deleteConceptGroupModal.deleteButton',
    }),
    submittingCancelButtonText: formatMessage({
      id: 'conceptGroups.deleteConceptGroupModal.cancelButton',
    }),
    submittingSaveButtonText: formatMessage({
      id: 'conceptGroups.deleteConceptGroupModal.submitting',
    }),
    successCloseButtonText: formatMessage({
      id: 'conceptGroups.deleteConceptGroupModal.closeButton',
    }),
    submittingErrorTitle: formatMessage({
      id: 'conceptGroups.deleteConceptGroupModal.submittingError',
    }),
    successTitle: formatMessage({ id: 'conceptGroups.deleteConceptGroupModal.success.title' }),
    successText: formatMessage(
      { id: 'conceptGroups.deleteConceptGroupModal.success.text' },
      { conceptGroupName: name }
    ),
  };

  return (
    <ApiWorkflowDialog
      id="deleteConceptGroupDialog"
      title={
        isInUse
          ? formatMessage({ id: 'conceptGroups.deleteConceptGroupModal.inUseTitle' })
          : formatMessage({ id: 'conceptGroups.deleteConceptGroupModal.title' })
      }
      onClose={closeDialog}
      onSubmit={onSubmit}
      isSubmitting={isSubmitting}
      dialogMessages={dialogMessages}
      saveButtonColor="destructive"
      submittingError={submittingError}
      isInitialLoading={isInitialLoading}
      isValidForSubmit={isValidForSubmit}
      hideDisplayButtonsOnSuccess
    >
      <DialogMessageBox
        id="delete-concept-group-message-box"
        title={
          isInUse
            ? formatMessage({ id: 'conceptGroups.deleteConceptGroupModal.inUseError' })
            : formatMessage({ id: 'conceptGroups.deleteConceptGroupModal.confirmTitle' })
        }
        type={isInUse ? 'warning' : 'error'}
        variant="outlined"
        renderSubmitted={
          !isInUse ? (
            <Stack gap={4}>
              <Typography color="error">
                {formatMessage({ id: 'conceptGroups.deleteConceptGroupModal.actionUndone' })}
              </Typography>
              <Typography>
                {formatMessage(
                  { id: 'conceptGroups.deleteConceptGroupModal.description' },
                  { conceptGroupName: <b>{name}</b> }
                )}
              </Typography>
            </Stack>
          ) : (
            <Typography>
              {formatMessage({ id: 'conceptGroups.deleteConceptGroupModal.inUseDescription' })}
            </Typography>
          )
        }
      />
    </ApiWorkflowDialog>
  );
};

export default DeleteConceptGroupDialog;
